import { Pause, Play } from '@/components/ui/icons';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import type { AttachmentDTO } from '@clanfire/shared';
import { cn } from '@/lib/utils';
import { formatDuration, WAVEFORM_BARS } from './VoiceRecorder';

/** Only one voice message plays at a time. */
let playing: HTMLAudioElement | null = null;

const SPEEDS = [1, 1.5, 2] as const;

function decodeWaveform(encoded: string | null): number[] {
  if (!encoded) return Array.from({ length: WAVEFORM_BARS }, () => 0.35);
  try {
    const bytes = Array.from(atob(encoded), (c) => c.charCodeAt(0) / 255);
    return bytes.length ? bytes : Array.from({ length: WAVEFORM_BARS }, () => 0.35);
  } catch {
    return Array.from({ length: WAVEFORM_BARS }, () => 0.35);
  }
}

/** A voice message: play/pause, a waveform that fills as it plays (click to jump), and playback speed. */
export function VoiceMessage({ attachment }: { attachment: AttachmentDTO }) {
  const audio = useRef<HTMLAudioElement>(null);
  const [isPlaying, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [speed, setSpeed] = useState<(typeof SPEEDS)[number]>(1);
  const bars = useMemo(() => decodeWaveform(attachment.waveform), [attachment.waveform]);
  const durationMs = attachment.durationMs ?? 0;
  const progress = durationMs ? Math.min(1, (position * 1000) / durationMs) : 0;

  useEffect(() => {
    const el = audio.current;
    if (!el) return;
    const onTime = () => setPosition(el.currentTime);
    const onEnd = () => {
      setPlaying(false);
      setPosition(0);
    };
    const onPause = () => setPlaying(false);
    const onPlay = () => setPlaying(true);
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('ended', onEnd);
    el.addEventListener('pause', onPause);
    el.addEventListener('play', onPlay);
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('ended', onEnd);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('play', onPlay);
      if (playing === el) playing = null;
    };
  }, []);

  useEffect(() => {
    if (audio.current) audio.current.playbackRate = speed;
  }, [speed]);

  const toggle = () => {
    const el = audio.current;
    if (!el) return;
    if (!el.paused) return el.pause();
    if (playing && playing !== el) playing.pause();
    playing = el;
    el.playbackRate = speed;
    void el.play().catch(() => setPlaying(false));
  };

  /** Jumps to a point in the recording (where the waveform was clicked). */
  const seek = (fraction: number) => {
    const el = audio.current;
    if (!el || !durationMs) return;
    const to = Math.max(0, Math.min(1, fraction)) * (durationMs / 1000);
    try {
      el.currentTime = to;
      setPosition(to);
    } catch {
      // Some recordings can't be jumped around in until they've loaded; playing still works.
    }
  };

  const onWaveClick = (e: MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    seek((e.clientX - rect.left) / rect.width);
  };
  const onWaveKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = 5000 / Math.max(durationMs, 1);
    if (e.key === 'ArrowRight') seek(progress + step);
    else if (e.key === 'ArrowLeft') seek(progress - step);
    else return;
    e.preventDefault();
  };

  return (
    <div className="flex w-full max-w-sm items-center gap-3 rounded-2xl border border-line bg-sunken py-2 pr-3 pl-2">
      <audio ref={audio} src={attachment.url} preload="metadata" />
      <button
        type="button"
        onClick={toggle}
        aria-label={isPlaying ? 'Pause voice message' : `Play voice message, ${formatDuration(durationMs)}`}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent text-accent-fg transition-colors hover:bg-accent-hover"
      >
        {isPlaying ? <Pause size={16} /> : <Play size={16} className="translate-x-px" />}
      </button>
      <div className="min-w-0 flex-1">
        <div
          role="slider"
          tabIndex={0}
          aria-label="Position in the voice message"
          aria-valuemin={0}
          aria-valuemax={Math.round(durationMs / 1000)}
          aria-valuenow={Math.round(position)}
          aria-valuetext={`${formatDuration(position * 1000)} of ${formatDuration(durationMs)}`}
          onClick={onWaveClick}
          onKeyDown={onWaveKey}
          className="flex h-8 cursor-pointer items-center gap-[2px] rounded focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:outline-none"
        >
          {bars.map((level, i) => (
            <span
              key={i}
              className={cn('min-w-[2px] flex-1 rounded-full transition-colors', i / bars.length < progress ? 'bg-accent' : 'bg-fg-muted/40')}
              style={{ height: `${Math.max(10, level * 100)}%` }}
            />
          ))}
        </div>
        <p className="mt-0.5 text-[11px] text-fg-muted tabular-nums">
          {isPlaying || position > 0 ? `${formatDuration(position * 1000)} / ${formatDuration(durationMs)}` : formatDuration(durationMs)}
        </p>
      </div>
      <button
        type="button"
        onClick={() => setSpeed((s) => SPEEDS[(SPEEDS.indexOf(s) + 1) % SPEEDS.length]!)}
        aria-label={`Playback speed ${speed}×`}
        className="shrink-0 rounded-full bg-hover px-2 py-0.5 text-[11px] font-bold text-fg-soft tabular-nums hover:text-fg"
      >
        {speed}×
      </button>
    </div>
  );
}
